import { c as _c } from "react/compiler-runtime";
import { postHostedDocumentsByIdUploadBySideMutation } from '@onefootprint/fetch-hosted';
import { useMutation } from '@tanstack/react-query';
import { getLogger } from '@/idv2/utils/logger';
import { DOC_UPLOAD_TIMEOUT_MS } from '../constants';
import { bytesToMegabytes } from '../utils/capture';
type UploadVideoArgs = {
  documentId: string;
  documentKind: 'identity' | 'selfie';
  videoFile: Blob;
  authToken: string;
};
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'upload-video'
});

// Only a request that got no answer, or a server error, can go better a second time: a 4xx fails the
// same way again, and a timed-out upload already made the user wait a minute.
const isWorthRetrying = (error: unknown) => error instanceof TypeError || ((error as {
  status?: number;
} | null)?.status ?? 0) >= 500;

/**
 * The clip must land before the selfie's /process, which can complete the document. Never throws;
 * `retry` means a second attempt could succeed.
 */
const useUploadVideo = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = postHostedDocumentsByIdUploadBySideMutation();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    mutateAsync
  } = useMutation(t0);
  let t1;
  if ($[1] !== mutateAsync) {
    t1 = async t2 => {
      const {
        documentId,
        documentKind,
        videoFile,
        authToken
      } = t2;
      const clip = {
        documentKind,
        sizeMb: bytesToMegabytes(videoFile.size),
        type: videoFile.type
      };
      const startedAt = Date.now();
      ;
      try {
        await mutateAsync({
          body: {
            fileData: videoFile
          },
          path: {
            id: documentId,
            side: "selfie_video"
          },
          headers: {
            "X-Fp-Authorization": authToken
          },
          signal: AbortSignal.timeout(DOC_UPLOAD_TIMEOUT_MS)
        });
        logInfo("Selfie video uploaded", {
          ...clip,
          durationMs: Date.now() - startedAt
        });
        return "uploaded";
      } catch (t3) {
        const error = t3;
        logWarn("Selfie video upload failed", error, {
          ...clip,
          durationMs: Date.now() - startedAt
        });
        return isWorthRetrying(error) ? "retry" : "failed";
      }
    };
    $[1] = mutateAsync;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export default useUploadVideo;